/**
 * EU VAT ID format validation
 *
 * Validates VAT identification numbers for all EU member states
 * based on official format rules per country.
 *
 * Reference: https://ec.europa.eu/taxation_customs/vies/faq.html
 */

import { EU_VAT_PATTERNS } from '@insio/countries';
import { type ValidationResult } from './company-id';

const EU_COUNTRY_CODES = Object.keys(EU_VAT_PATTERNS);

/** Greece uses "EL" in VAT but "GR" in ISO — accept both by remapping the prefix. */
function remapGreekPrefix(vatId: string): string {
  return vatId.startsWith('GR') ? `EL${vatId.substring(2)}` : vatId;
}

/**
 * Validate an EU VAT ID format.
 * Accepts full VAT ID with country prefix (e.g., "CZ12345678", "DE123456789").
 * No reason to remap "GR" to "EL" here — the official EU VAT ID prefix is "EL" for Greece and we expect the input to be in that form.
 */
export function validateVatId(vatId: string): ValidationResult {
  if (vatId.length < 3) {
    return { valid: false, error: 'VAT ID is too short' };
  }

  const countryCode = vatId.substring(0, 2);
  const pattern = (EU_VAT_PATTERNS as Readonly<Record<string, RegExp>>)[countryCode];

  if (!pattern) {
    return { valid: false, error: `Unknown EU country code: "${countryCode}"` };
  }

  if (!pattern.test(vatId)) {
    return { valid: false, error: `Invalid VAT ID format for ${countryCode}` };
  }

  return { valid: true };
}

/**
 * Shorthand: returns true if VAT ID format is valid.
 */
export function isValidVatId(vatId: string): boolean {
  return validateVatId(vatId).valid;
}

/**
 * Extract country code from a full VAT ID.
 * Returns null if format is invalid.
 */
export function extractVatIdCountryCode(vatId: string): string | null {
  // We get EL but since we want to return the ISO code, we remap it back to GR for Greece. Sinco ISO is still GR
  const trimmed = remapGreekPrefix(vatId.trim().toUpperCase());
  if (trimmed.length < 3) return null;

  const countryCode = trimmed.substring(0, 2);
  return EU_COUNTRY_CODES.includes(countryCode) ? countryCode : null;
}

/**
 * List of all supported EU country codes for VAT validation.
 */
export function getSupportedVatCountryCodes(): string[] {
  return [...EU_COUNTRY_CODES];
}
